import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, TrendingUp, CheckCircle, Rocket, Brain } from 'lucide-react';

interface VisionAmbitionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function VisionAmbitionModal({ isOpen, onClose }: VisionAmbitionModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-indigo-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
                    <Rocket className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                      Vision & Ambition
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      IA = copilote de décision - 300k → 600k en 12 mois
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                <h3 className="text-3xl font-bold text-center text-white mb-4">
                  Notre Vision : IA = Copilote de Décision
                </h3>
                <p className="text-gray-300 text-lg text-center leading-relaxed">
                  <strong className="text-white">Le chatbot propose, l'humain dispose.</strong> 
                  L'IA est notre copilote, pas notre pilote. C'est cette philosophie qui guide tout ce que nous faisons.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-7 h-7 text-indigo-400" />
                    Objectif Chiffré
                  </h4>
                  <p className="text-gray-300 mb-4 text-2xl font-bold text-center text-indigo-400">
                    300k → 600k en 12 mois
                  </p>
                  <p className="text-gray-300 text-center">
                    Avec la <strong className="text-white">même équipe</strong>, en travaillant autrement grâce à l'IA.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-7 h-7 text-purple-400" />
                    Notre Promesse
                  </h4>
                  <p className="text-gray-300 mb-4 text-2xl font-bold text-center text-purple-400">
                    100% des clients font plus de cash
                  </p>
                  <p className="text-gray-300 text-center">
                    Grâce à l'IA intégrée dans nos solutions.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Brain className="w-7 h-7 text-blue-400" />
                  Le Mantra
                </h4>
                <p className="text-gray-300 text-lg text-center italic">
                  "IA = copilote de décision : le chatbot propose, l'humain dispose."
                </p>
                <p className="text-gray-300 mt-4">
                  Ce mantra est <strong className="text-white">affiché partout</strong> dans notre département. 
                  C'est notre philosophie, notre façon de travailler, notre différence.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-7 h-7 text-green-400" />
                  Comment On Y Arrive
                </h4>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">Cadre PROGRAM-IA</strong> - Processus structuré et rapide</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">POC 48h</strong> - Validation rapide des concepts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">IA intégrée</strong> - Chatbot standard dans chaque app</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span><strong className="text-white">ROI mesurable</strong> - 100% des clients font plus de cash</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












